import { meetingService, type Meeting } from '../../../services/meeting.service.ts';
import axios from 'axios';
import { isLiveSessionExpired, isMeetingLiveActive } from './meetingLiveExpiry.ts';

export const ONLINE_MEETING_LINK_VALIDITY_MS = 60 * 60 * 1000;

export interface MeetingLinkMeta {
  createdAt: number;
}

const LOCAL_PREFIX = 'tbkmeet_meeting_link_';
const SESSION_PREFIX = 'tbkmeet_meeting_link_session_';

function localKey(meetingId: string): string {
  return `${LOCAL_PREFIX}${meetingId}`;
}

function sessionKey(meetingId: string): string {
  return `${SESSION_PREFIX}${meetingId}`;
}

export function registerMeetingLink(meetingId: string, createdAt = Date.now()): void {
  const meta: MeetingLinkMeta = { createdAt };
  localStorage.setItem(localKey(meetingId), JSON.stringify(meta));
}

export function readLocalMeetingLinkMeta(meetingId: string): MeetingLinkMeta | null {
  try {
    const raw = localStorage.getItem(localKey(meetingId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MeetingLinkMeta;
    if (typeof parsed.createdAt === 'number') return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

export function writeSessionMeetingLinkMeta(meetingId: string, meta: MeetingLinkMeta): void {
  sessionStorage.setItem(sessionKey(meetingId), JSON.stringify(meta));
}

export function readSessionMeetingLinkMeta(meetingId: string): MeetingLinkMeta | null {
  try {
    const raw = sessionStorage.getItem(sessionKey(meetingId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MeetingLinkMeta;
    if (typeof parsed.createdAt === 'number') return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

export function isLinkExpiredByCreatedAt(createdAt: number, now = Date.now()): boolean {
  return now - createdAt >= ONLINE_MEETING_LINK_VALIDITY_MS;
}

function shouldExpireInstantOnlineMeeting(meeting: Meeting): boolean {
  if (meeting.meetingFormat === 'IN_PERSON') return false;
  if (meeting.isInstant) return true;
  return !meeting.scheduledAt;
}

function isMeetingLinkExpiredApiError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  return (
    error.response?.status === 410 ||
    error.response?.data?.error === 'MEETING_LINK_EXPIRED' ||
    error.response?.data?.error === 'MEETING_LIVE_EXPIRED'
  );
}

async function fetchMeetingByCodeOrId(meetingId: string): Promise<Meeting | null> {
  try {
    return await meetingService.getMeetingByCode(meetingId);
  } catch (error) {
    if (isMeetingLinkExpiredApiError(error)) {
      throw error;
    }
    return null;
  }
}

export async function resolveMeetingLinkCreatedAt(meetingId: string): Promise<number | null> {
  const meeting = await fetchMeetingByCodeOrId(meetingId);
  if (meeting) {
    if (!shouldExpireInstantOnlineMeeting(meeting)) return null;
    return new Date(meeting.createdAt).getTime();
  }

  const local = readLocalMeetingLinkMeta(meetingId);
  if (local) return local.createdAt;

  const session = readSessionMeetingLinkMeta(meetingId);
  if (session) return session.createdAt;

  return null;
}

export type MeetingLinkAccessResult =
  | { allowed: true }
  | { allowed: false; expired: true }
  | { allowed: false; expired: false; reason: 'not_found' | 'in_person' };

export async function validateMeetingLinkAccess(meetingId: string): Promise<MeetingLinkAccessResult> {
  try {
    const meeting = await fetchMeetingByCodeOrId(meetingId);

    if (meeting) {
      if (meeting.meetingFormat === 'IN_PERSON') {
        return { allowed: false, expired: false, reason: 'in_person' };
      }
      if (meeting.status === 'LIVE' && isLiveSessionExpired(meeting)) {
        return { allowed: false, expired: true };
      }
      if (!shouldExpireInstantOnlineMeeting(meeting)) {
        return { allowed: true };
      }
      const createdAt = new Date(meeting.createdAt).getTime();
      if (isLinkExpiredByCreatedAt(createdAt)) {
        return { allowed: false, expired: true };
      }
      return { allowed: true };
    }
  } catch (error) {
    if (isMeetingLinkExpiredApiError(error)) {
      return { allowed: false, expired: true };
    }
  }

  const local = readLocalMeetingLinkMeta(meetingId);
  const session = readSessionMeetingLinkMeta(meetingId);
  const createdAt = local?.createdAt ?? session?.createdAt ?? null;

  if (createdAt == null) {
    return { allowed: true };
  }

  if (isLinkExpiredByCreatedAt(createdAt)) {
    return { allowed: false, expired: true };
  }

  return { allowed: true };
}

export async function findActiveInstantMeetingCode(): Promise<string | null> {
  try {
    const meetings = await meetingService.getMeetings();
    const active = meetings
      .filter(
        (m) =>
          m.isInstant &&
          isMeetingLiveActive(m) &&
          m.meetingFormat !== 'IN_PERSON'
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];

    if (!active) return null;

    const access = await validateMeetingLinkAccess(active.meetingCode);
    if (!access.allowed && access.expired) return null;

    return active.meetingCode;
  } catch {
    return null;
  }
}

export async function createInstantOnlineMeeting(): Promise<string> {
  const existingCode = await findActiveInstantMeetingCode();
  if (existingCode) {
    return existingCode;
  }

  const fallbackCreatedAt = Date.now();

  try {
    const meeting = await meetingService.createMeeting({
      title: 'Anlık toplantı',
      meetingFormat: 'ONLINE',
      meetingType: 'VIDEO',
      isInstant: true,
      duration: 60,
    });
    const code = meeting.meetingCode || meeting.id;
    registerMeetingLink(code, new Date(meeting.createdAt).getTime());
    return code;
  } catch {
    const meetingId = Math.random().toString(36).substring(2, 11);
    registerMeetingLink(meetingId, fallbackCreatedAt);
    return meetingId;
  }
}

export function getMeetingLinkRemainingMs(createdAt: number, now = Date.now()): number {
  return Math.max(0, ONLINE_MEETING_LINK_VALIDITY_MS - (now - createdAt));
}
